import {
  AlertCircle,
  BookOpen,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Home,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

interface GuidesScreenProps {
  onBack: () => void
}

const guides = [
  {
    id: 'buying',
    title: "First Home Buyer's Guide",
    icon: Home,
    description: 'Your complete guide to buying your first home in New Zealand',
    sections: [
      {
        title: 'Getting Pre-Approved',
        content:
          "Before you start house hunting, get pre-approval from your bank or mortgage broker. This tells you exactly how much you can borrow and shows sellers you're a serious buyer.",
      },
      {
        title: 'Understanding the Process',
        content:
          'In NZ, property purchases typically involve: making an offer (conditional or unconditional), completing due diligence, going unconditional, and settlement (usually 4-6 weeks after).',
      },
      {
        title: 'Due Diligence Checklist',
        content:
          "Get a building inspection, LIM report, title search, and valuation. Check for any easements, covenants, or consent issues. Don't skip these steps—they protect you from costly surprises.",
      },
      {
        title: 'First Home Grants & Schemes',
        content:
          'You may be eligible for the First Home Grant (up to $10,000) or First Home Loan scheme. Check with Kāinga Ora for eligibility requirements.',
      },
    ],
  },
  {
    id: 'selling',
    title: 'Selling Your Property',
    icon: TrendingUp,
    description: 'Maximise your sale price and find the right buyer',
    sections: [
      {
        title: 'Choosing Your Agent',
        content:
          'Select a licensed REINZ member agent with local expertise. Ask about their marketing strategy, commission structure, and recent sales in your area.',
      },
      {
        title: 'Methods of Sale',
        content:
          'Common methods include: Auction (competitive bidding, set date), Deadline Sale (offers by a deadline), Tender (sealed bids), or By Negotiation (price guide with negotiation).',
      },
      {
        title: 'Preparing Your Home',
        content:
          'First impressions matter. Declutter, deep clean, and consider staging. Fix minor repairs and refresh paint if needed. A well-presented home typically sells faster and for more.',
      },
      {
        title: 'Understanding Fees & Costs',
        content:
          'Budget for agent commission (typically 2-4% + GST), legal fees, marketing costs, and any outstanding rates or mortgages to be repaid at settlement.',
      },
    ],
  },
  {
    id: 'process',
    title: 'The NZ Property Process',
    icon: BookOpen,
    description: 'Step-by-step guide to property transactions',
    sections: [
      {
        title: 'Step 1: Find & View Properties',
        content:
          'Search online, attend open homes, and register your interest with agents. Take notes and photos (with permission) to compare properties.',
      },
      {
        title: 'Step 2: Make an Offer',
        content:
          'Your offer will typically be conditional on finance approval, building inspection, and LIM report. Your lawyer reviews the Sale & Purchase Agreement before you sign.',
      },
      {
        title: 'Step 3: Complete Conditions',
        content:
          "You'll have a set period (usually 10-15 working days) to complete your due diligence and confirm finance. Once all conditions are met, you go unconditional.",
      },
      {
        title: 'Step 4: Settlement',
        content:
          'Your lawyer handles the legal transfer. On settlement day, funds are exchanged, the title is transferred, and you get the keys to your new home!',
      },
    ],
  },
]

const tips = [
  { type: 'do', text: 'Always use a licensed real estate agent' },
  { type: 'do', text: 'Get independent legal advice before signing' },
  { type: 'do', text: 'Complete thorough due diligence' },
  { type: 'dont', text: 'Skip the building inspection' },
  { type: 'dont', text: 'Make unconditional offers without finance confirmed' },
  { type: 'dont', text: 'Rush major decisions' },
]

export function GuidesScreen({ onBack }: GuidesScreenProps) {
  const [selectedGuide, setSelectedGuide] = useState<(typeof guides)[0] | null>(null)

  if (selectedGuide) {
    return (
      <div className="flex flex-col min-h-full pb-4">
        <button
          onClick={() => setSelectedGuide(null)}
          className="flex items-center gap-2 text-primary mb-4 hover:underline"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Guides
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <selectedGuide.icon className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">{selectedGuide.title}</h1>
            <p className="text-sm text-muted-foreground">{selectedGuide.description}</p>
          </div>
        </div>

        <div className="space-y-4">
          {selectedGuide.sections.map((section, index) => (
            <Card key={index} className="p-4">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {index + 1}
                </span>
                {section.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
            </Card>
          ))}
        </div>

        <Card className="p-4 bg-secondary/50 mt-6">
          <p className="text-xs text-muted-foreground text-center">
            This guide is for informational purposes only and does not constitute legal or financial
            advice. Always seek independent professional advice.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-full pb-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-primary mb-4 hover:underline"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-xl font-semibold text-foreground mb-2">Guides & Resources</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Educational content to help you navigate the NZ property market
      </p>

      {/* Guide Cards */}
      <div className="space-y-3 mb-8">
        {guides.map((guide) => {
          const Icon = guide.icon
          return (
            <Card
              key={guide.id}
              className="p-4 cursor-pointer card-interactive"
              onClick={() => setSelectedGuide(guide)}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">{guide.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{guide.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </Card>
          )
        })}
      </div>

      {/* Quick Tips */}
      <h2 className="text-sm font-semibold text-foreground mb-3">Quick Tips</h2>
      <div className="grid grid-cols-1 gap-2 mb-6">
        {tips.map((tip, index) => (
          <div
            key={index}
            className={`flex items-center gap-3 p-3 rounded-lg ${
              tip.type === 'do' ? 'bg-emerald-50' : 'bg-red-50'
            }`}
          >
            {tip.type === 'do' ? (
              <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            )}
            <p className={`text-sm ${tip.type === 'do' ? 'text-emerald-800' : 'text-red-800'}`}>
              {tip.text}
            </p>
          </div>
        ))}
      </div>

      {/* Find an Agent CTA */}
      <Card className="p-4 bg-primary text-primary-foreground">
        <h3 className="font-medium mb-2">Ready to Get Started?</h3>
        <p className="text-sm opacity-80 mb-4">
          Connect with a trusted REINZ member agent in your area.
        </p>
        <Button variant="secondary" className="w-full" onClick={onBack}>
          Find an Agent
        </Button>
      </Card>
    </div>
  )
}
